import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DestroyRef, Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertMessageComponent } from '../../shared/components/alert-message/alert-message';
import { AlertMessageTypes } from '../../shared/components/alert-message/alert-message.types';
import { AlertSuccess } from '../../shared/components/alert-message/classes/alert-success.class';
import { AlertInfo } from '../../shared/components/alert-message/classes/alert-info.class';
import { AlertWarning } from '../../shared/components/alert-message/classes/alert-warning.class';
import { AlertError } from '../../shared/components/alert-message/classes/alert-error.class';

interface AlertOptions {
  code?: string;
  title?: string;
  timeOut?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AlertMessagingService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);

  private overlayRef: OverlayRef | null = null;
  private subscription: { unsubscribe(): void } | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.closeExisting());
  }

  /**
   * Shows a green success alert in the bottom-right corner.
   * @param message Body text describing what succeeded.
   * @param opts.title Heading text. Defaults to `'Success'`.
   * @param opts.code Optional status/error code shown before the title.
   * @param opts.timeOut Auto-dismiss delay in ms. Defaults to `3000`. Pass `0` to keep it open until manually closed.
   * @example
   * this.alertService.success('Student record saved.');
   * this.alertService.success('Record saved.', { code: '200', title: 'Done', timeOut: 3000 });
   */
  success(message: string, opts: AlertOptions = {}) {
    const { code = '', title = 'Success', timeOut = 3000 } = opts;
    this.openAlert(new AlertSuccess(code, title, message), timeOut || null);
  }

  /**
   * Shows a blue informational alert in the bottom-right corner.
   * @param message Body text with the information to convey.
   * @param opts.title Heading text. Defaults to `'Information'`.
   * @param opts.code Optional status/error code shown before the title.
   * @param opts.timeOut Auto-dismiss delay in ms. Defaults to `3000`. Pass `0` to keep it open until manually closed.
   * @example
   * this.alertService.info('Sync runs every 5 minutes.');
   * this.alertService.info('New batch available.', { title: 'Heads up', timeOut: 3000 });
   */
  info(message: string, opts: AlertOptions = {}) {
    const { code = '', title = 'Information', timeOut = 3000 } = opts;
    this.openAlert(new AlertInfo(code, title, message), timeOut || null);
  }

  /**
   * Shows an amber warning alert in the bottom-right corner.
   * @param message Body text describing the potential issue.
   * @param opts.title Heading text. Defaults to `'Warning'`.
   * @param opts.code Optional status/error code shown before the title.
   * @param opts.timeOut Auto-dismiss delay in ms. Defaults to `3000`. Pass `0` to keep it open until manually closed.
   * @example
   * this.alertService.warning('Attendance is below threshold.');
   * this.alertService.warning('Unsaved changes.', { title: 'Watch out', timeOut: 0 });
   */
  warning(message: string, opts: AlertOptions = {}) {
    const { code = '', title = 'Warning', timeOut = 3000 } = opts;
    this.openAlert(new AlertWarning(code, title, message), timeOut || null);
  }

  /**
   * Shows a red error alert in the bottom-right corner.
   * @param message Body text describing what went wrong — or an `HttpErrorResponse` whose status code and body message are extracted automatically.
   * @param opts.title Heading text. Defaults to `'Error'`.
   * @param opts.code Optional status/error code. Ignored when `message` is an `HttpErrorResponse` (the HTTP status is used instead).
   * @param opts.timeOut Auto-dismiss delay in ms. Defaults to `3000`. Pass `0` to keep it open until manually closed.
   * @example
   * this.alertService.error('Failed to load data.');
   * this.alertService.error('Request failed.', { code: '500', timeOut: 0 });
   * this.alertService.error(httpErrorResponse);
   */
  error(message: HttpErrorResponse | string, opts: AlertOptions = {}) {
    const { title = 'Error', timeOut = 3000 } = opts;
    let code = opts.code ?? '';
    let body: string;

    if (message instanceof HttpErrorResponse) {
      if (!code && message.status) {
        code = String(message.status);
      }
      body =
        message.error?.message ??
        message.message ??
        'An unexpected error occurred. Please try again later.';
    } else {
      body = message;
    }
    this.openAlert(new AlertError(code, title, body), timeOut || null);
  }

  /**
   * Low-level method to show any alert type directly.
   * Prefer the typed helpers (`success`, `info`, `warning`, `error`) for everyday use.
   * Calling this dismisses any currently visible alert first.
   * @param messageInstance An instance of `AlertSuccess`, `AlertInfo`, `AlertWarning`, or `AlertError`.
   * @param timeOut Auto-dismiss delay in ms. `null` keeps the alert open until the user closes it manually.
   */
  openAlert(messageInstance: AlertMessageTypes, timeOut: number | null = null) {
    this.closeExisting();

    // The toast positions itself bottom-right via its own :host styles, so no position
    // strategy is needed — CDK only manages the portal lifecycle (attach/detach/dispose).
    this.overlayRef = this.overlay.create();

    const portalInjector = Injector.create({
      providers: [
        { provide: 'ALERT_MESSAGE_DATA', useValue: messageInstance },
        { provide: 'TIME_OUT', useValue: timeOut },
      ],
      parent: this.injector,
    });

    const ref = this.overlayRef.attach(
      new ComponentPortal(AlertMessageComponent, null, portalInjector),
    );
    this.subscription = ref.instance.close.subscribe(() => this.closeExisting());
  }

  private closeExisting() {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
